import type { PrintLayoutKind, RawPrintSettings } from "../types";
import { sanitizeStoredFontValue } from "./fonts";
import { plainTextToHeaderHtml, sanitizeHeaderHtml } from "./sanitizeHeaderHtml";

export type HeaderLayout = "inline" | "inline_apart" | "center" | "left" | "right" | "full";
export type FooterAlign = "left" | "center" | "right";
export type PaperSize = "A4" | "A5" | "Letter";

export interface PrintLayout {
  kind: PrintLayoutKind;
  paper: PaperSize;
  marginTopMm: number;
  marginSideMm: number;
  marginBottomMm: number;
  backgroundImageUrl: string | null;
  headerLayout: HeaderLayout;
  headerImageUrl: string | null;
  headerLogoHeightPx: number;
  headerHtml: string;
  footerText: string | null;
  footerImageUrl: string | null;
  footerAlign: FooterAlign;
  fontId: string;
  fontSizePt: number;
  lineHeight: number;
  textColor: string;
  titleColor: string;
  headerTextColor: string;
  footerTextColor: string;
  showIssuerBlock: boolean;
  stampShowCrmv: boolean;
  stampShowMapa: boolean;
}

export const PRINT_LAYOUT_DEFAULTS: Omit<PrintLayout, "kind"> = {
  paper: "A4",
  marginTopMm: 10,
  marginSideMm: 15,
  marginBottomMm: 12,
  backgroundImageUrl: null,
  headerLayout: "center",
  headerImageUrl: null,
  headerLogoHeightPx: 60,
  headerHtml: "",
  footerText: null,
  footerImageUrl: null,
  footerAlign: "center",
  fontId: "helvetica",
  fontSizePt: 10,
  lineHeight: 1.5,
  textColor: "#1F1D1B",
  titleColor: "#1F1D1B",
  headerTextColor: "#1F1D1B",
  footerTextColor: "#1F1D1B",
  showIssuerBlock: true,
  stampShowCrmv: true,
  stampShowMapa: true,
};

const HEADER_LAYOUTS: readonly HeaderLayout[] = ["inline", "inline_apart", "center", "left", "right", "full"];
const FOOTER_ALIGNS: readonly FooterAlign[] = ["left", "center", "right"];
const PAPERS: readonly PaperSize[] = ["A4", "A5", "Letter"];
const LINE_HEIGHTS = [1.15, 1.5, 2] as const;
const HEADER_HTML_TAG = /<\/?(div|p|br|b|strong|i|em|u|span)\b[^>]*>/i;
const HEX = /^#[0-9a-fA-F]{6}$/;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

function num(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === "number" ? value : value != null && value !== "" ? Number(value) : NaN;
  return Number.isFinite(parsed) ? clamp(parsed, min, max) : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function color(value: unknown, fallback: string): string {
  return typeof value === "string" && HEX.test(value.trim()) ? value.trim().toUpperCase() : fallback;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

export function resolvePrintLayout(raw: RawPrintSettings | null | undefined, kind: PrintLayoutKind): PrintLayout {
  const settings = raw ?? {};
  const prefix = kind === "term" ? "term_" : "";
  const get = (field: string) => settings[`${prefix}${field}`];
  const d = PRINT_LAYOUT_DEFAULTS;

  const rawHeader = text(get("header_text"));
  const headerHtml = rawHeader
    ? HEADER_HTML_TAG.test(rawHeader) ? sanitizeHeaderHtml(rawHeader) : plainTextToHeaderHtml(rawHeader)
    : "";

  const lineHeightRaw = num(get("line_height"), d.lineHeight, 1, 2);
  const lineHeight = LINE_HEIGHTS.reduce((best, candidate) =>
    Math.abs(candidate - lineHeightRaw) < Math.abs(best - lineHeightRaw) ? candidate : best, LINE_HEIGHTS[0]);

  return {
    kind,
    paper: oneOf(get("paper_size"), PAPERS, d.paper),
    marginTopMm: num(get("margin_top_mm"), d.marginTopMm, 5, 40),
    marginSideMm: num(get("margin_side_mm"), d.marginSideMm, 5, 40),
    marginBottomMm: num(get("margin_bottom_mm"), d.marginBottomMm, 8, 40),
    backgroundImageUrl: text(get("background_image_url")),
    headerLayout: oneOf(get("header_layout"), HEADER_LAYOUTS, d.headerLayout),
    headerImageUrl: text(get("header_image_url")),
    headerLogoHeightPx: num(get("header_logo_height_px"), d.headerLogoHeightPx, 32, 120),
    headerHtml,
    footerText: text(get("footer_text")),
    footerImageUrl: text(get("footer_image_url")),
    footerAlign: oneOf(get("footer_layout"), FOOTER_ALIGNS, d.footerAlign),
    fontId: sanitizeStoredFontValue(text(get("text_font_family"))),
    fontSizePt: num(get("font_size_pt"), d.fontSizePt, 9, 12),
    lineHeight,
    textColor: color(get("text_color"), d.textColor),
    titleColor: color(get("title_color"), d.titleColor),
    headerTextColor: color(get("header_text_color"), d.headerTextColor),
    footerTextColor: color(get("footer_text_color"), d.footerTextColor),
    showIssuerBlock: bool(get("show_issuer_block"), d.showIssuerBlock),
    stampShowCrmv: bool(get("stamp_show_crmv"), d.stampShowCrmv),
    stampShowMapa: bool(get("stamp_show_mapa"), d.stampShowMapa),
  };
}
